<template>
  <div id="page" class="container-fluid">
    <div class="row header mx-4 my-4">
      <div class="col header-row">
        <h1>EMR Patient Search</h1>
      </div>
      <div class="row filter-row mb-1">
        <b-form-group class="mr-2" label="First Name" :state="stateForField('firstName')" :invalid-feedback="errorForField('firstName')">
          <b-form-input class="emr-search-border" v-model="firstName"></b-form-input>
        </b-form-group>
        <b-form-group class="mr-2" label="Last Name" :state="stateForField('lastName')" :invalid-feedback="errorForField('lastName')">
          <b-form-input class="emr-search-border" v-model="lastName"></b-form-input>
        </b-form-group>
        <b-form-group class="mr-2" label="DOB" :state="stateForField('dateOfBirth')" :invalid-feedback="errorForField('dateOfBirth')">
          <b-form-group class="mb-0"
                        :invalid-feedback="errorForField('birthDate')"
                        :state="stateForField('birthDate')">
            <datepicker locale="en-US" :typeable="true" class="form-control datepicker" v-model="dateOfBirth" @input="formatDate"></datepicker>
          </b-form-group>
        </b-form-group>
<div class="search-button">
        <b-button title="Search" variant="orange" @click.prevent="searchEmrPatients()">
          Search
        </b-button>
</div>
      </div>
    </div>
    <div class="container-fluid">
      <div class="row data-table-header">
        <div class="col-2">Patient Name</div>
        <div class="col-2">DOB</div>
        <div class="col-2">MRN</div>
        <div class="col-2">Patient ID</div>
        <div class="col-4"></div>
      </div>
      <div class="row data-table-row" v-for="patient in patients" :key="patient.patientIdFromOrg">
        <div class="column col-2">{{ patient.name.first + ' ' + patient.name.last}}</div>
        <div class="column col-2">{{ formatBirthDate(patient.birthDate)}}</div>
        <div class="column col-2">{{ patient.mrn}}</div>
        <div class="column col-2">{{ patient.patientIdFromOrg}}</div>
        <div class="col-4 text-right">
          <button title="Import" type="button" class="btn btn-outline-orange m-1 wide-button" @click.prevent="importPatient(patient.patientIdFromOrg)">
            <font-awesome-icon class="orange mr-2" :icon="['far', 'square-arrow-up-right']"/>Import
          </button>
        </div>
      </div>
      <b-alert ref="alertContainer" :show="alertCountDown" dismissible fade :variant="alertVariant" @dismissed="alertCountDown=0">
        {{ alertMessage }}
      </b-alert>
    </div>
    <loading-modal :visible="showingLoadingModal" :return-focus="$refs.searchInput"
                   @update:visible="visible => showingLoadingModal = visible"></loading-modal>
  </div>
</template>
<script>
  import moment from 'moment-timezone'
  import api from '@/services/api'
  import ErrorsMixin from '@/mixins/ErrorsMixin'
  import Datepicker from 'vuejs-datepicker'
  import LoadingModal from '../components/LoadingModal'

  export default {
    name: 'search-patient',
    components: { LoadingModal, Datepicker },
    mixins: [ErrorsMixin],

    data () {
      return {
        patients: [],
        errors: [],
        firstName: '',
        lastName: '',
        dateOfBirth: '',
        isLoading: false,
        showingLoadingModal: false,
        formattedDateOfBirth: '',
        patient: {
          patientIdFromOrg: ''
        },
        alertCountDown: 0,
        alertMessage: '',
        alertVariant: 'danger'
      }
    },

    methods: {
      searchEmrPatients (silent = false) {
        if (!this.validate()) {
          return
        }
        if (!silent) {
          this.showingLoadingModal = true
        }
        if (this.isLoading) {
          return
        }
        const parameters = {
          firstName: this.firstName,
          lastName: this.lastName,
          dateOfBirth: this.formattedDateOfBirth
        }
        this.isLoading = true
        api.post('/Dashboard/SearchPatients', parameters).then(response => {
          this.patients = response.data.patients
          this.alertCountDown = 0
          if (!response.data.patients || response.data.patients.length === 0) {
            this.addErrorForField('exception', 'No patients found')
            this.$scrollToTop()
            this.alertMessage = 'No patients found matching the search criteria.'
            this.alertCountDown = 20
          }
          this.isLoading = false
        }).catch(error => {
          this.isLoading = false
          if (!this.$store.canHandleNetworkError(error)) {
            this.setFromApiErrorResponse(error)
            this.$log.error('Error attempting to search patients: ' + error.message, error)
          }
        }).then(() => {
          // Workaround for https://github.com/bootstrap-vue/bootstrap-vue/issues/1826
          setTimeout(() => {
            this.showingLoadingModal = false
          }, 100)
        })
      },
      formatBirthDate (dob) {
        return moment(dob).utc().format('MM/DD/YYYY')
      },
      formatDate (date) {
        if (date) {
          const formattedDate = new Date(date).toISOString().slice(0, 10)
          this.formattedDateOfBirth = formattedDate
        } else {
          this.formattedDateOfBirth = ''
        }
      },
      importPatient (patientIdFromOrg) {
        const parameters = {
          patientIdFromOrg: patientIdFromOrg
        }
        api.post('Dashboard/ImportPatient', parameters).then(response => {
          this.patient = response.data
          this.$router.push({ name: 'patient-detail', params: { id: response.data.patientId } })
        }).catch(error => {
          if (!this.$store.canHandleNetworkError(error)) {
            this.setFromApiErrorResponse(error)
            this.$log.error('Error attempting import patient: ' + error.message, error)
          }
        })
      },
      validate () {
        this.clearAllErrors()

        if (!this.firstName) {
          this.addErrorForField('firstName', 'You must provide a first name')
        }

        if (!this.lastName) {
          this.addErrorForField('lastName', 'You must provide a last name')
        }

        if (!this.dateOfBirth) {
          this.addErrorForField('dateOfBirth', 'You must provide a DOB')
        }
        return !this.hasErrors()
      }
    }
  }
</script>
<style scoped>
.search-button {
  display: flex;
  align-items: center;
  margin-top: 10px;
}
.header-row {
  margin-top: 28px;
}
.emr-search-border {
  border: 1px solid rgb(255, 70, 0);
  border-radius: 5px;
}
.form-control {
  border: 1px solid rgb(255, 70, 0);
  border-radius: 5px;
}
</style>
